import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  faBan,
  faCode,
  faRotateRight,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { List, ListItem } from '@/components/List';
import { Typography } from '@/components/Typography';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import authService from '@/data/services/auth.service';

interface TooManyAttemptsErrorProps {
  attempts: number;
  maxAttempts?: number;
  isDevelopment?: boolean;
  onRetry?: () => void;
}

export const TooManyAttemptsError: React.FC<TooManyAttemptsErrorProps> = ({
  attempts,
  maxAttempts = 3,
  isDevelopment = false,
  onRetry,
}) => {
  const { t } = useTranslation();

  const handleReset = () => {
    authService.clearAllAuthData();
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const steps = [
    {
      label: t('auth.error.attempts.scud'),
      description: t('auth.error.attempts.scud_description'),
    },
    {
      label: t('auth.error.attempts.browser'),
      description: t('auth.error.attempts.browser_description'),
    },
    {
      label: t('auth.error.attempts.contact'),
      description: t('auth.error.attempts.contact_description'),
    },
  ];

  return (
    <div className='flex min-h-svh flex-col items-center justify-center gap-4 p-4'>
      <div className='w-full max-w-xl rounded-xl border border-t-4 border-t-destructive bg-card p-6'>
        <div className='mb-6 flex items-center gap-4'>
          <FontAwesomeIcon icon={faBan} className='size-8 text-destructive' />
          <div className='flex flex-col items-start gap-2'>
            <Typography variant='h4' as='h1'>
              {t('auth.error.too_many_attempts')}
            </Typography>
            <div className='flex flex-wrap items-center gap-2'>
              <Badge variant='outline' className='border-destructive text-destructive'>
                {`${attempts}/${maxAttempts} ${t('auth.error.attempts.text')}`}
              </Badge>
              <Badge variant='outline'>{t('auth.error.attempts.loop')}</Badge>
            </div>
          </div>
        </div>

        <Alert variant='destructive' className='mb-6'>
          <AlertDescription>
            <Typography variant='paragraphSmall' color='destructive'>
              {t('auth.error.too_many_attempts_description', { attempts })}
            </Typography>
          </AlertDescription>
        </Alert>

        <Typography variant='h4' className='mb-4'>
          {t('auth.error.too_many_attempts_suggestion')} :
        </Typography>

        <List className='mb-6 flex flex-col gap-4'>
          {steps.map((step, index) => (
            <ListItem key={step.label} className='flex items-start gap-3'>
              <Badge
                variant={index === 0 ? 'default' : 'outline'}
                className='size-6 shrink-0 justify-center p-0'
              >
                {index + 1}
              </Badge>
              <div className='flex flex-col gap-1'>
                <Typography variant='paragraphSmallBold' color='foreground'>
                  {step.label}
                </Typography>
                <Typography variant='paragraphMini'>{step.description}</Typography>
              </div>
            </ListItem>
          ))}
        </List>

        <div className='mb-2 flex gap-2'>
          <Button className='flex-1' onClick={handleReset}>
            <FontAwesomeIcon icon={faRotateRight} />
            {t('auth.error.reset_retry')}
          </Button>

          {isDevelopment && (
            <Button variant='outline' className='flex-1'>
              <FontAwesomeIcon icon={faCode} />
              {t('auth.error.ignore_auth')}
            </Button>
          )}
        </div>

        <Button
          variant='outline'
          className='w-full'
          onClick={() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
          }}
        >
          <FontAwesomeIcon icon={faShieldCheck} />
          {t('auth.error.clear_data')}
        </Button>

        {isDevelopment && (
          <Alert variant='info' className='mt-4'>
            <AlertDescription>
              <Typography variant='paragraphMini' color='info'>
                {t('auth.error.development_mode', {
                  code: 'VITE_APP_SKIP_AUTH=true',
                })}
              </Typography>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Typography variant='paragraphMini' className='text-center'>
        {t('auth.error.contact_admin')}
      </Typography>
    </div>
  );
};
